import { useMemo } from 'react';
// routes
import { paths } from 'src/routes/paths';
// locales
import { useLocales } from 'src/locales';
// components
import { Iconify } from 'src/components/iconify';
import { ENVIRONMENT } from 'src/config-global';
import { CONSTANT_VARIABLES } from 'src/constants/constants-variables';
import { METABASE_BASE_URL, METABASE_DASHBOARD_IDS } from 'src/constants/metabase-config';
import { isRoleAdmin, isUserAgent, isUserAuditor } from 'src/utils/check-user-is-admin';
import producerPromotion from 'src/assets/icons/promotions/producer_promotion.svg';
import allProducts from 'src/assets/icons/promotions/all_products.svg';
// ----------------------------------------------------------------------

const icon = (name: string | React.ReactNode) =>
  typeof name === 'string' ? <Iconify icon={name} /> : name;

const ICONS = {
  // catalog: icon('ic:baseline-menu-book'),
  catalog: icon(<img src={allProducts} alt="All Products" width={20} height={20} />),
  orders: icon('mdi:invoice-text-edit-outline'),
  // producers: icon('fluent-emoji-high-contrast:grapes'),
  producers: icon(<img src={producerPromotion} alt="Producer Promotion" width={16} height={16} />),
  admin: icon('mdi-archive-settings'),
  userTypes: icon('ic:outline-supervised-user-circle'),
  orderTypes: icon('ic:baseline-card-giftcard'),
  adminOrders: icon('mdi-archive-settings'),
  adminProducts: icon('mdi-format-list-bulleted-square'),
  statistics: icon('mdi:chart-line-variant'),
  tool: icon('mdi:wrench-outline'),
  agents: icon('solar:users-group-rounded-bold-duotone'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();

  const isDevelopment = ENVIRONMENT === CONSTANT_VARIABLES.DEVELOP;
  const isProductionOrStage = new Set([
    CONSTANT_VARIABLES.STAGE,
    CONSTANT_VARIABLES.PRODUCTION,
  ]).has(ENVIRONMENT);
  const isAgent = isUserAgent();
  const isAdmin = isRoleAdmin();
  const isAuditor = isUserAuditor();

  const statisticsMenu = useMemo(() => {
    const dashboards = [
      {
        title: t('profitability'),
        path: paths.admin.statisticTypes.profitability,
      },
      {
        title: t('current_situation'),
        path: 'https://data.natives.it/dashboard/34-situazione-attuale',
      },
      {
        title: t('margins_and_costs'),
        path: 'https://data.natives.it/dashboard/35-margini-costi',
      },
      {
        title: t('sales_statistics'),
        path: 'https://data.natives.it/dashboard/22-statistiche-vendite',
      },
      { title: t('finance'), path: 'https://data.natives.it/dashboard/36-finanza' },
      {
        title: t('stock_optimization'),
        path: 'https://data.natives.it/dashboard/30-ottimizzazione-stock',
      },
      {
        title: t('price_range_and_stock'),
        path: 'https://data.natives.it/dashboard/29-fasce-prezzo-stock',
      },
      {
        title: t('producers_at_risk'),
        path: 'https://data.natives.it/dashboard/24-produttori-a-rischio',
      },
      {
        title: t('client_evaluation_qrfm'),
        path: 'https://data.natives.it/dashboard/11-valutazione-clienti-qrfm',
      },
    ];
    return [
      {
        title: t('statistics'),
        path: dashboards.map((d) => d.path),
        icon: ICONS.statistics,
        children: dashboards,
      },
    ];
  }, [t]);

  const customerItems = useMemo(
    () => [
      {
        title: t('catalog'),
        path: paths.customer.catalog,
        icon: ICONS.catalog,
        disclaimerButton: !isAdmin,
      },
      {
        title: t('producers'),
        path: paths.customer.producers,
        icon: ICONS.producers,
      },
      {
        title: t('order_list'),
        path: paths.customer.orders.root,
        icon: ICONS.orders,
      },
    ],
    [isAdmin, t],
  );

  const commonAdminItems = useMemo(
    () => [
      {
        title: t('dashboard'),
        path: [
          paths.admin.dashboard.operations,
          paths.admin.dashboard.overview,
          paths.admin.reconciliation.root,
        ],
        icon: ICONS.admin,
        children: [
          { title: t('operativa'), path: paths.admin.dashboard.operations },
          { title: t('logistics_and_accounting'), path: paths.admin.dashboard.overview },
          { title: t('reconciliation'), path: paths.admin.reconciliation.root },
        ],
      },
      {
        title: t('clients'),
        path: [
          paths.admin.users.root,
          paths.admin.clients.root,
          paths.admin.companies.root,
          paths.admin.reservations.root,
          paths.admin.discounts.root,
          paths.admin.tastings.root,
        ],
        icon: ICONS.userTypes,
        children: [
          { title: t('users'), path: paths.admin.users.root },
          { title: t('clients'), path: paths.admin.clients.root },
          { title: t('companies'), path: paths.admin.companies.root },
          { title: t('reservations'), path: paths.admin.reservations.root },
          { title: t('discounts'), path: paths.admin.discounts.root },
          { title: t('tastings'), path: paths.admin.tastings.root },
          {
            title: t('client_portfolio_analysis'),
            path: 'https://data.natives.it/dashboard/26-analisi-portafoglio-clienti',
          },
          {
            title: t('client_analysis'),
            path: `${METABASE_BASE_URL}/${METABASE_DASHBOARD_IDS.client}`,
          },
        ],
      },
      {
        title: t('agents'),
        path: [paths.admin.agents.root],
        icon: ICONS.agents,
        children: [
          { title: t('agents'), path: paths.admin.agents.root },
          {
            title: t('agent_analysis'),
            path: `${METABASE_BASE_URL}/${METABASE_DASHBOARD_IDS.agent}`,
          },
        ],
      },
      {
        title: t('orders'),
        path: [
          paths.admin.orderTypes.clientOrders.root,
          paths.admin.orderTypes.producerOrders.root,
        ],
        icon: ICONS.admin,
        children: [
          { title: t('client_orders'), path: paths.admin.orderTypes.clientOrders.root },
          { title: t('producer_orders'), path: paths.admin.orderTypes.producerOrders.root },
        ],
      },
      {
        title: t('products'),
        path: [
          paths.admin.products.root,
          paths.admin.wineTypes.root,
          paths.admin.grapeTypes.root,
          paths.admin.selections.root,
        ],
        icon: ICONS.adminProducts,
        children: [
          { title: t('all_the_products'), path: paths.admin.products.root },
          {
            title: t('reorder-products'),
            path: 'https://data.natives.it/dashboard/33-ordine-produttori',
          },
          { title: t('wine_types'), path: paths.admin.wineTypes.root },
          { title: t('grape_types'), path: paths.admin.grapeTypes.root },
          { title: t('selections'), path: paths.admin.selections.root },
          {
            title: t('product_analysis'),
            path: `${METABASE_BASE_URL}/${METABASE_DASHBOARD_IDS.product}`,
          },
        ],
      },
      {
        title: t('producers'),
        path: [
          paths.admin.orderTypes.producerOrders.root,
          paths.admin.producers.root,
          paths.admin.territory.root,
          paths.admin.territory.new,
        ],
        icon: ICONS.producers,
        children: [
          { title: t('producer_orders'), path: paths.admin.orderTypes.producerOrders.root },
          { title: t('producers'), path: paths.admin.producers.root },
          { title: t('territories'), path: paths.admin.territory.root },
          {
            title: t('producer_analysis'),
            path: `${METABASE_BASE_URL}/${METABASE_DASHBOARD_IDS.producer}`,
          },
        ],
      },
      ...statisticsMenu,
      {
        title: t('tools'),
        path: [
          paths.admin.targets.root,
          paths.admin.controls.root,
          paths.admin.catalogPdf.root,
          paths.admin.dashboard.overlays.root,
          paths.admin.dashboard.table_text,
        ],
        icon: ICONS.tool,
        children: [
          {
            title: t('targets'),
            path: paths.admin.targets.root,
          },
          {
            title: t('logistica_sync'),
            path: paths.admin.controls.root,
          },
          {
            title: t('catalog_pdf'),
            path: paths.admin.catalogPdf.root,
          },
          { title: t('overlays'), path: paths.admin.dashboard.overlays.root },
          { title: t('table_text_title'), path: paths.admin.dashboard.table_text },
        ],
      },
    ],

    [t, statisticsMenu],
  );

  const commonAgentItems = useMemo(
    () => [
      {
        title: t('dashboard'),
        path: paths.agent.dashboard,
        icon: ICONS.admin,
      },
      {
        title: t('catalog'),
        path: paths.customer.catalog,
        icon: ICONS.catalog,
      },
      {
        title: t('clients'),
        path: [paths.admin.clients.root, paths.admin.clients.new],
        icon: ICONS.userTypes,
        children: [
          { title: t('clients'), path: paths.admin.clients.root },
          { title: t('new_client'), path: paths.admin.clients.new },
        ],
      },
      {
        title: t('orders'),
        path: [paths.admin.orderTypes.clientOrders.root],
        icon: ICONS.admin,

        children: [{ title: t('clients'), path: paths.admin.orderTypes.clientOrders.root }],
      },
    ],
    [t],
  );

  const commonAuditorItems = useMemo(
    () => [
      {
        title: t('orders'),
        path: [paths.admin.orderTypes.producerOrders.root],
        icon: ICONS.admin,
        children: [{ title: t('providers'), path: paths.admin.orderTypes.producerOrders.root }],
      },
    ],
    [t],
  );
  const developmentAdminItems = useMemo(() => commonAdminItems, [commonAdminItems]);

  const prodORStageAdminItems = useMemo(() => [...commonAdminItems], [commonAdminItems]);

  const data = useMemo(() => {
    if (isProductionOrStage && isAdmin) {
      return [
        { subheader: t('overview'), items: customerItems },
        { subheader: t('admin'), items: prodORStageAdminItems },
      ];
    }
    if (isProductionOrStage && !isAdmin && !isAgent && !isAuditor) {
      return [{ subheader: t('overview'), items: customerItems }];
    }
    if (isProductionOrStage && isAgent) {
      return [{ subheader: t('overview'), items: commonAgentItems }];
    }
    if (isDevelopment && isAgent) {
      return [{ subheader: t('overview'), items: commonAgentItems }];
    }
    if (isProductionOrStage && isAuditor) {
      return [{ subheader: t('overview'), items: commonAuditorItems }];
    }
    if (isDevelopment && isAuditor) {
      return [{ subheader: t('overview'), items: commonAuditorItems }];
    }
    if (isDevelopment && isAdmin) {
      return [
        { subheader: t('overview'), items: customerItems },
        { subheader: t('admin'), items: developmentAdminItems },
      ];
    }
    return [{ subheader: t('overview'), items: customerItems }];
  }, [
    t,
    isAdmin,
    isAgent,
    isAuditor,
    isDevelopment,
    isProductionOrStage,
    customerItems,
    developmentAdminItems,
    prodORStageAdminItems,
    commonAgentItems,
    commonAuditorItems,
  ]);

  return data;
}
